/**
 * Camera-animation timings for the map, with a reduced-motion guard.
 *
 * Mapbox honours the OS "reduce motion" setting for its OWN gesture animations,
 * but NOT for programmatic `easeTo` / `fitBounds` calls — those animate unless
 * we pass `duration: 0` ourselves. `cameraDuration` centralises that so every
 * map camera move degrades to an instant jump-cut under reduced motion
 * (WCAG 2.3.3 Animation from Interactions).
 *
 * The durations also live here (not inlined per call site) so they don't drift
 * across the map camera's call sites.
 */

/** One-time cold fit-to-all-zones on first load of /map. */
export const INITIAL_FIT_DURATION_MS = 1200;

/** Focusing the deal a `?location=` deep link resolved to (see PromotedLocationRedirect). */
export const DEAL_FOCUS_DURATION_MS = 800;

/** Zoom the same move lands at — close enough to read the deal's surroundings, as the search does. */
export const DEAL_FOCUS_ZOOM = 14;

/** Viewport inset for a fit-to-bounds move, so the map chrome never covers the fitted shape. */
export const FIT_PADDING = { top: 80, bottom: 60, left: 60, right: 60 };

/** True when the user has asked the OS to minimise non-essential motion. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Resolve a Mapbox camera-animation duration: the preferred duration normally,
 * or 0 (instant jump) when the user prefers reduced motion.
 */
export function cameraDuration(preferredMs: number): number {
  return prefersReducedMotion() ? 0 : preferredMs;
}
